import { useEffect, useMemo, useRef } from 'react';
import { appRoutes } from '@/routes/routes';
import {
  getEmployeePageFiltersFromSearch,
  mergeEmployeePageFiltersIntoSearch,
} from '@/utils/employeePageUrlFilters';
import { normalizeSearchString } from '@/utils/urlSearch';
import { findSelectOptionByValue } from '@/utils/selectOptions';

/** Routes that share `company` + `team` query params. */
export const EMPLOYEE_PAGE_PATHNAMES = Object.freeze(
  new Set([
    appRoutes().addEmployee,
    appRoutes().employeeList,
    appRoutes().teamList,
  ])
);

/**
 * Full wiring for Add Employee / Employee List: refs, POP/PUSH sync, REPLACE
 * write, and one-time hydration from `?company=` / `?team=` (avoids fighting
 * {@link useEmployeePageUrlWrite} the same way as teams-reporting).
 *
 * @param {object} args
 * @param {Array<{ label: string, value: string }>} args.companyOptions
 * @param {Array<{ label: string, value: string }>} args.teamOptions
 * @param {{ label: string, value: string } | null} args.selectedCompany
 * @param {{ label: string, value: string } | null} args.selectedTeam
 * @param {string | null | undefined} [args.defaultCompanyId] Applied when there is no company in the URL and nothing is selected yet.
 * @param {string | null | undefined} [args.defaultTeamId] Applied when there is no team in the URL and nothing is selected yet.
 * @param {string | null | undefined} [args.teamDefaultOnlyWhenCompanyId] Only apply defaultTeamId if selected company matches this id (typically active company).
 * @param {string} [args.searchTerm] Current search-box value. Omit entirely if the page has no search box (leaves `?search` untouched by this hook). Pair with {@link useUrlParamSync} for the URL → state direction.
 * @param {string} [args.roleFilter] Current role-pill value. Omit entirely if the page has no role filter.
 * @param {string} [args.roleDefaultValue] Value that means "no filter" and should be omitted from the URL (default `'All'`).
 */
export function useEmployeePageCompanyTeamUrlSync({
  companyOptions,
  teamOptions,
  selectedCompany,
  selectedTeam,
  setSelectedCompany,
  setSelectedTeam,
  history,
  location,
  defaultCompanyId,
  defaultTeamId,
  teamDefaultOnlyWhenCompanyId,
  searchTerm,
  roleFilter,
  roleDefaultValue,
}) {
  const filtersFromUrl = useMemo(
    () => getEmployeePageFiltersFromSearch(location.search),
    [location.search]
  );

  const companyOptionsRef = useRef([]);
  const teamOptionsRef = useRef([]);

  useEffect(() => {
    companyOptionsRef.current = companyOptions;
  }, [companyOptions]);

  useEffect(() => {
    teamOptionsRef.current = teamOptions;
  }, [teamOptions]);

  useEmployeePagePopSync({
    history,
    companyOptionsRef,
    teamOptionsRef,
    setSelectedCompany,
    setSelectedTeam,
  });

  useEmployeePageUrlWrite({
    selectedCompanyId: selectedCompany?.value,
    selectedTeamId: selectedTeam?.value,
    searchTerm,
    roleFilter,
    roleDefaultValue,
    history,
    location,
  });

  const prevSearchRef = useRef(location.search);

  useEffect(() => {
    const prev = getEmployeePageFiltersFromSearch(prevSearchRef.current);
    const now = getEmployeePageFiltersFromSearch(location.search);
    prevSearchRef.current = location.search;

    const hadFilter = Boolean(prev.company || prev.team);
    const nowHasFilter = Boolean(now.company || now.team);
    if (hadFilter && !nowHasFilter && (selectedCompany || selectedTeam)) {
      setSelectedCompany(null);
      setSelectedTeam(null);
    }
  }, [
    location.search,
    selectedCompany,
    selectedTeam,
    setSelectedCompany,
    setSelectedTeam,
  ]);

  useEffect(() => {
    if (!companyOptions.length) return;
    if (selectedCompany) return;
    if (filtersFromUrl.company) {
      const opt = companyOptions.find(
        (c) => c.value === filtersFromUrl.company
      );
      if (opt) {
        setSelectedCompany(opt);
        return;
      }
    }
  }, [
    companyOptions,
    filtersFromUrl.company,
    selectedCompany,
    setSelectedCompany,
  ]);

  useEffect(() => {
    if (!companyOptions.length) return;
    if (selectedCompany) return;
    if (filtersFromUrl.company) return;
    if (!defaultCompanyId) return;
    const opt = findSelectOptionByValue(companyOptions, defaultCompanyId);
    if (opt) setSelectedCompany(opt);
  }, [
    companyOptions,
    filtersFromUrl.company,
    selectedCompany,
    defaultCompanyId,
    setSelectedCompany,
  ]);

  useEffect(() => {
    if (!teamOptions.length) return;
    if (!selectedCompany?.value) return;
    if (selectedTeam) return;
    if (filtersFromUrl.team) {
      const opt = teamOptions.find((t) => t.value === filtersFromUrl.team);
      if (opt) {
        setSelectedTeam(opt);
      }
    }
  }, [
    teamOptions,
    filtersFromUrl.team,
    selectedCompany?.value,
    selectedTeam,
    setSelectedTeam,
  ]);

  useEffect(() => {
    if (!teamOptions.length) return;
    if (!selectedCompany?.value) return;
    if (selectedTeam) return;
    if (filtersFromUrl.team) return;
    if (!defaultTeamId || !teamDefaultOnlyWhenCompanyId) return;
    if (selectedCompany.value !== teamDefaultOnlyWhenCompanyId) return;
    const opt = findSelectOptionByValue(teamOptions, defaultTeamId);
    if (opt) setSelectedTeam(opt);
  }, [
    teamOptions,
    filtersFromUrl.team,
    selectedCompany?.value,
    selectedTeam,
    defaultTeamId,
    teamDefaultOnlyWhenCompanyId,
    setSelectedTeam,
  ]);
}

/**
 * Applies URL query → local state on browser back/forward and in-app
 * `PUSH` navigation. Ignores `REPLACE` (from {@link useEmployeePageUrlWrite}) so
 * local dropdown state does not fight the URL.
 *
 * `companyOptionsRef` / `teamOptionsRef` must point at the latest option arrays.
 * Team is applied in a macrotask so `teamOptionsRef` can reflect the company
 * after `setSelectedCompany` schedules an update.
 */
export function useEmployeePagePopSync({
  history,
  companyOptionsRef,
  teamOptionsRef,
  setSelectedCompany,
  setSelectedTeam,
  pathnames = EMPLOYEE_PAGE_PATHNAMES,
}) {
  useEffect(() => {
    return history.listen((nextLocation, action) => {
      if (!pathnames.has(nextLocation.pathname)) return;
      if (action !== 'POP' && action !== 'PUSH') return;

      const parsed = getEmployeePageFiltersFromSearch(
        nextLocation.search || ''
      );

      if (!parsed.company) {
        setSelectedCompany(null);
        setSelectedTeam(null);
        return;
      }

      const companyOpts = companyOptionsRef.current;
      if (companyOpts?.length) {
        const companyFromUrl = companyOpts.find(
          (c) => c.value === parsed.company
        );
        if (companyFromUrl) {
          setSelectedCompany((prev) => {
            if (prev?.value === companyFromUrl.value) return prev;
            return companyFromUrl;
          });
        }
      }

      if (!parsed.team) {
        setSelectedTeam(null);
        return;
      }

      setTimeout(() => {
        const teamOpts = teamOptionsRef.current;
        if (!teamOpts?.length) return;
        const teamFromUrl = teamOpts.find((t) => t.value === parsed.team);
        if (teamFromUrl) {
          setSelectedTeam((prev) => {
            if (prev?.value === teamFromUrl.value) return prev;
            return teamFromUrl;
          });
        }
      }, 0);
    });
  }, [
    history,
    companyOptionsRef,
    teamOptionsRef,
    setSelectedCompany,
    setSelectedTeam,
    pathnames,
  ]);
}

/**
 * Writes `company` + `team` (and, if the page manages them, `search` +
 * `role`) into `location.search` via `history.replace`, preserving unrelated
 * keys. Skips when the normalized query would not change.
 *
 * Skips while the URL still has `company` / `team` that are not reflected in
 * state yet (initial load / refresh). Otherwise the first effect pass would
 * strip the query string before hydration runs. `search` / `role` have no
 * such hydration step (no options list to resolve against), so they're
 * always written once company/team clear that gate.
 *
 * `searchTerm` / `roleFilter`: pass `undefined` (i.e. omit) if the page has
 * no such control at all — this hook then leaves that URL key untouched
 * rather than clearing it, so e.g. Add Employee's link-out doesn't strip a
 * `search` value Employee List will still need when the user navigates back.
 */
export function useEmployeePageUrlWrite({
  selectedCompanyId,
  selectedTeamId,
  searchTerm,
  roleFilter,
  roleDefaultValue = 'All',
  history,
  location,
}) {
  useEffect(() => {
    const parsed = getEmployeePageFiltersFromSearch(location.search);

    const companyPendingHydration =
      Boolean(parsed.company) && !selectedCompanyId;
    const teamPendingHydration = Boolean(parsed.team) && !selectedTeamId;

    if (companyPendingHydration || teamPendingHydration) {
      return;
    }

    const filters = {
      companyId: selectedCompanyId ?? '',
      teamId: selectedTeamId ?? '',
    };
    if (searchTerm !== undefined) filters.search = searchTerm || '';
    if (roleFilter !== undefined) {
      filters.role = roleFilter === roleDefaultValue ? '' : roleFilter;
    }

    const nextSearch = mergeEmployeePageFiltersIntoSearch(
      location.search,
      filters
    );

    if (
      normalizeSearchString(location.search) ===
      normalizeSearchString(nextSearch)
    ) {
      return;
    }

    history.replace({
      pathname: location.pathname,
      search: nextSearch,
    });
  }, [
    selectedCompanyId,
    selectedTeamId,
    searchTerm,
    roleFilter,
    roleDefaultValue,
    history,
    location.pathname,
    location.search,
  ]);
}
